import { useEffect, useState } from "react";
import useXRErrors from "./useXRErrors";
import useUserDevice from "./useUserDevice";
import { AppPermissionStatus } from "@webcore/core/permissions/permissions.status";

export interface XRErrorConfig {
  defaultMessage: string;
  iOSMessage: string;
  androidMessage?: string;
}

/**
 * Provides user-friendly guidance for XR issues
 */
export function useXRErrorGuidance(customErrors?: {
  deniedCamera?: XRErrorConfig;
  deniedMotion?: XRErrorConfig;
  denied?: XRErrorConfig;
  notDefined?: XRErrorConfig;
  notSupported?: XRErrorConfig;
}) {
  const { xrPermissionStatus, xrOtherStatus, xrSupportStatus } = useXRErrors();
  const { device } = useUserDevice();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const isAndroid = device === "Android";

  const androidGuidance = {
    camera: ` <ul class="pt-8 px-10 text-left flex flex-col gap-1 text-[18px] list-decimal">
                <li>
                  Tap the <span class="font-bold"> icon</span> next
                  to the website address
                </li>
                <li>
                  Select <span class="font-bold">Permissions</span>
                </li>
                <li>
                  Enable <span class="font-bold">Camera</span> and/or  <span class="font-bold">Microphone</span>
                </li>
              </ul>`,
    webxr: `<ul class="pt-8 px-10 text-left flex flex-col gap-1 text-[22px] list-decimal">
                <li>
                  Tap the <span class="font-bold">lock icon</span> next
                  to the website address
                </li>
                <li>
                  Select <span class="font-bold">Permissions</span>
                </li>
                <li>
                  Enable <span class="font-bold">Augmented Reality</span>
                </li>
              </ul>`,
  };

  const defaultErrors = {
    deniedCamera: {
      defaultMessage: "Allow camera access in the app settings.",
      iOSMessage:
        "Camera & Microphone <br />access is required.<br /><br />Quit and reopen the <br />browser to allow access.",
      androidMessage: androidGuidance.camera,
    },
    deniedMotion: {
      defaultMessage:
        "Motion & Orientation <br />access is required. <br /><br />Quit and reopen the <br />browser to allow access.",
    },
    denied: {
      defaultMessage: "Allow camera access in the app settings.",
      androidMessage: androidGuidance.webxr,
    },
    notDefined: {
      defaultMessage: "WebXR is not defined in this browser.",
    },
    notSupported: {
      defaultMessage: "WebXR is not supported on this device.",
    },
  };

  const errors = {
    deniedCamera: {
      ...defaultErrors.deniedCamera,
      ...customErrors?.deniedCamera,
    },
    deniedMotion: {
      ...defaultErrors.deniedMotion,
      ...customErrors?.deniedMotion,
    },
    denied: { ...defaultErrors.denied, ...customErrors?.denied },
    notDefined: { ...defaultErrors.notDefined, ...customErrors?.notDefined },
    notSupported: {
      ...defaultErrors.notSupported,
      ...customErrors?.notSupported,
    },
  };

  useEffect(() => {
    setErrorMessage(null);

    switch (xrPermissionStatus) {
      case AppPermissionStatus.DENIED_CAMERA: {
        const config = errors.deniedCamera;
        if (isAndroid) {
          setErrorMessage(config.defaultMessage + androidGuidance.camera);
        } else if (device === "iOS") {
          setErrorMessage(config.iOSMessage);
        } else {
          setErrorMessage(config.defaultMessage);
        }
        break;
      }

      case AppPermissionStatus.DENIED_MOTION: {
        const config = errors.deniedMotion;
        setErrorMessage(config.defaultMessage);
        break;
      }

      case AppPermissionStatus.DENIED: {
        const config = errors.denied;
        if (isAndroid) {
          setErrorMessage(config.defaultMessage + androidGuidance.webxr);
        } else {
          setErrorMessage(config.defaultMessage);
        }
        break;
      }

      case AppPermissionStatus.GRANTED:
      case AppPermissionStatus.PROMPT:
      default:
        break;
    }

    if (xrOtherStatus === false) {
      const config = errors.notDefined;
      setErrorMessage(config.defaultMessage);
    }

    if (xrSupportStatus === false) {
      const config = errors.notSupported;
      setErrorMessage(config.defaultMessage);
    }
  }, [
    xrPermissionStatus,
    device,
    xrOtherStatus,
    xrSupportStatus,
    isAndroid,
    errors,
  ]);

  return {
    errorMessage,
    permissionStatus: xrPermissionStatus,
    supportStatus: xrSupportStatus,
    otherStatus: xrOtherStatus,
  };
}
